import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Chip } from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import PageHeader from '../../components/PageHeader';
import ImageUploader from '../../components/ImageUploader';
import OnboardingQuestions, { missingRequired } from '../../components/OnboardingQuestions';
import CityPicker from '../../components/CityPicker';
import api from '../../api/endpoints';
import { useAuth } from '../../context/AuthContext';
import { requestLocationPermission, getCurrentPosition } from '../../services/location';
import { colors } from '../../theme';
import { PARTNER_TYPES, VEHICLE_OPTIONS } from '../../constants';
import VehicleForm from '../../components/VehicleForm';
import { isRoadType, mixesSides, sideOf } from '../../utils/partnerTypes';

const STEPS = ['Your work', 'Business', 'Address'];

/**
 * "Sell on KAAMWALA" self-onboarding. Step 1 picks the profession/business
 * type, step 2 collects the business details, step 3 the address.
 */
const PartnerRegisterPage = () => {
  const navigate = useNavigate();

  /**
   * Backing out of step 1 returns to the customer app.
   *
   * It used to sign the person out - the only way off this page - and any
   * account that had ever been upgraded to `partner` was sent straight back
   * here on the next login. Dropping the partner mode leaves the account
   * untouched and simply puts them back on the customer side.
   */
  const leave = () => {
    setMode('user');
    navigate('/home', { replace: true });
  };
  const { setPartner, refreshProfile, setMode } = useAuth();

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  // Services the partner typed in themselves ("Birds seller"): pending until admin approves.
  const [customCats, setCustomCats] = useState([]);
  const [customName, setCustomName] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [customNote, setCustomNote] = useState('');
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');

  /** Where the business actually is - see resolveCoordinates for the order of trust. */
  const [cityCoords, setCityCoords] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsState, setGpsState] = useState('idle');

  useEffect(() => {
    if (step !== 2 || gps) return;
    (async () => {
      setGpsState('asking');
      const ok = await requestLocationPermission();
      const fix = ok ? await getCurrentPosition() : null;
      if (fix && fix.coordinates) { setGps(fix.coordinates); setGpsState('ok'); } else setGpsState('denied');
    })();
  }, [step, gps]);

  /*
   * Device position, then the picked city, then a geocode of the typed
   * address. The app's "delivering to" point is never used: it defaults to
   * Delhi and placed every new partner there regardless of where they were.
   */
  const resolveCoordinates = async () => {
    if (gps) return gps;
    if (cityCoords) return cityCoords;
    try {
      const q = [form.line1, form.landmark, form.city, form.district, form.state, form.pincode].filter(Boolean).join(', ');
      const res = await api.maps.places({ q });
      const first = res.data && res.data[0];
      if (first && first.placeId) {
        const d = await api.maps.placeDetails(first.placeId);
        if (d.data && d.data.coordinates) return d.data.coordinates;
      }
    } catch (e) { /* fall through */ }
    return null;
  };
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState('');
  const [gallery, setGallery] = useState([]);
  const [form, setForm] = useState({
    partnerType: '',
    // Every kind of work they do; partnerType is the first one picked.
    partnerTypes: [],
    businessName: '',
    description: '',
    phone: '',
    line1: '',
    landmark: '',
    city: '',
    district: '',
    state: '',
    pincode: '',
    vehicleType: 'cab',
    vehicleModel: '',
    vehicleNumber: '',
    vehicle: {},
  });

  /**
   * Two lists, because the partner needs both: the categories that match the
   * work they picked on step 1, and - since our mapping cannot anticipate every
   * business - the full customer-facing catalogue behind a toggle.
   */
  useEffect(() => {
    api.categories.list({ limit: 100, grouped: true })
      .then((res) => setGroups(res.data || []))
      .catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    if (!form.partnerType) { setCategories([]); return; }
    const typesKeyNow = form.partnerTypes.join(',');
    // "Other" has no mapping yet, so it is offered every category.
    api.categories.list(form.partnerTypes.includes('other') ? { limit: 200 } : { limit: 150, partnerType: typesKeyNow })
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.partnerTypes.join(',')]);

  const typeLabel = form.partnerTypes.map((v) => (PARTNER_TYPES.find((t) => t.value === v) || {}).label).filter(Boolean).join(', ');
  // The vehicle questions follow whichever road work they picked.
  const roadType = form.partnerTypes.includes('driver') ? 'driver' : form.partnerTypes.includes('courier') ? 'courier' : null;

  const toggleCategory = (id) => {
    setSelected((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
    setError('');
  };

  const requestCustom = async () => {
    const name = customName.trim();
    if (name.length < 2) { setError('Type the name of your service'); return; }
    setRequesting(true);
    setError('');
    try {
      const res = await api.categories.request({ name, partnerType: form.partnerType });
      const c = res.data;
      if (!categories.some((x) => x._id === c._id)) {
        setCustomCats((list) => (list.some((x) => x._id === c._id) ? list : [...list, c]));
      }
      setSelected((list) => (list.includes(c._id) ? list : [...list, c._id]));
      setCustomName('');
      setCustomNote(res.message);
    } catch (err) {
      setError(err.message);
    } finally { setRequesting(false); }
  };

  const next = () => {
    if (step === 0 && !form.partnerType) { setError('Choose what best describes you'); return; }
    if (step === 1) {
      if (form.businessName.trim().length < 2) { setError('Enter your business or work name'); return; }
      if (!selected.length) { setError('Select at least one category you serve'); return; }
      const missing = missingRequired(questions, answers);
      if (missing) { setError(`Please add: ${missing}`); return; }
    }
    setError('');
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    // Riders and couriers have no counter to visit - their city is enough.
    if (!roadType && form.line1.trim().length < 3) { setError('Enter your address'); return; }
    if (roadType && !(form.vehicle.number || form.vehicleNumber)) { setError('Enter your vehicle registration number'); return; }
    setLoading(true);
    setError('');
    try {
      const coordinates = await resolveCoordinates();
      if (!coordinates) {
        setError('We could not place your business on the map. Allow location access, or pick your city from the list.');
        setLoading(false);
        return;
      }
      const res = await api.auth.registerPartner({
        logo,
        gallery,
        businessName: form.businessName.trim(),
        partnerType: form.partnerType,
        partnerTypes: form.partnerTypes,
        onboarding: answers,
        categories: selected,
        primaryCategory: selected[0],
        description: form.description,
        phone: form.phone || undefined,
        address: {
          line1: form.line1,
          landmark: form.landmark,
          city: form.city,
          district: form.district,
          state: form.state,
          pincode: form.pincode,
        },
        coordinates,
        vehicleDetails: VEHICLE_OPTIONS[roadType] ? {
          ...form.vehicle,
          type: form.vehicleType,
          wheels: ['bike', 'scooter'].includes(form.vehicleType) ? 'two' : ['auto', 'erickshaw'].includes(form.vehicleType) ? 'three' : 'four',
          model: form.vehicle.model || form.vehicleModel,
          number: form.vehicle.number || form.vehicleNumber,
        } : undefined,
      });
      setPartner(res.data.partner);
      await refreshProfile();
      setStep(3);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 3) {
    return (
      <div className="center-page" style={{ minHeight: '100vh', background: '#fff' }}>
        <div style={{ maxWidth: 460 }}>
          <div
            className="pop"
            style={{
              width: 100, height: 100, borderRadius: 50, background: colors.successBg,
              display: 'grid', placeItems: 'center', fontSize: 44, margin: '0 auto',
            }}
          >
            🎊
          </div>
          <h1 className="h1" style={{ marginTop: 20 }}>Application submitted</h1>
          <p className="t-body" style={{ color: colors.muted, marginTop: 12, lineHeight: 1.6 }}>
            Our team reviews new businesses within 24 hours. You will get a notification the moment{' '}
            <strong style={{ color: colors.ink }}>{form.businessName}</strong> is approved — then you
            can add your products and services.
          </p>
          <Button
            title="Go to my dashboard"
            size="lg"
            full
            style={{ marginTop: 28 }}
            onClick={async () => { setMode('partner'); await refreshProfile(); navigate('/partner', { replace: true }); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.bg }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 20 }}>
        <PageHeader
          title="Join as Partner"
          subtitle={`Step ${step + 1} of 3 · ${step === 2 && roadType ? 'Your city' : STEPS[step]}`}
          onBack={step === 0 ? leave : () => setStep((s) => s - 1)}
        />

        <div style={{ height: 4, background: colors.line, borderRadius: 3, marginBottom: 22 }}>
          <div
            style={{
              height: 4, borderRadius: 3, background: colors.primary,
              width: `${((step + 1) / 3) * 100}%`, transition: 'width .3s cubic-bezier(.22,1,.36,1)',
            }}
          />
        </div>

        <div className="card sh-sm" style={{ padding: 20 }}>
          {step === 0 ? (
            <Reveal>
              <h2 className="h2">What describes your work?</h2>
              <p className="t-small" style={{ marginTop: 4, marginBottom: 16 }}>
                Pick every kind of work you do — a cab driver who is also an electrician picks both. This decides what you can list.
              </p>
              {PARTNER_TYPES.map((t, i) => {
                const active = form.partnerTypes.includes(t.value);
                // Rides and parcels cannot be combined with shop or service work.
                const blocked = !active && mixesSides(form.partnerTypes, t.value);
                return (
                  <Reveal key={t.value} delay={i * 30}>
                    <button
                      type="button"
                      disabled={blocked}
                      onClick={() => {
                        // Several kinds of work can be picked; clicking again removes one.
                        const list = active ? form.partnerTypes.filter((v) => v !== t.value) : [...form.partnerTypes, t.value];
                        const firstVehicle = !active && VEHICLE_OPTIONS[t.value]
                          ? VEHICLE_OPTIONS[t.value][0].value : '';
                        // Switching sides makes the old categories meaningless.
                        if (sideOf(list) !== sideOf(form.partnerTypes)) { setSelected([]); setCustomCats([]); }
                        setForm({
                          ...form,
                          partnerTypes: list,
                          partnerType: list[0] || '',
                          vehicleType: firstVehicle || form.vehicleType,
                        });
                        setError('');
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                        textAlign: 'left', padding: 12, marginBottom: 8, borderRadius: 16,
                        border: `1.4px solid ${active ? colors.primary : colors.line}`,
                        background: active ? colors.primaryLight : '#fff',
                        opacity: blocked ? 0.4 : 1,
                        cursor: blocked ? 'not-allowed' : 'pointer',
                        transition: 'border-color .16s ease, background .16s ease',
                      }}
                    >
                      <span style={{ fontSize: 26 }}>{t.icon}</span>
                      <span className="grow">
                        <span className="t-title" style={{ display: 'block' }}>{t.label}</span>
                        <span className="t-tiny">
                          {blocked
                            ? (isRoadType(t.value)
                              ? 'Not with shop or service work - a separate account is needed'
                              : 'Not with rides or parcels - a separate account is needed')
                            : t.hint}
                        </span>
                      </span>
                      <span
                        style={{
                          width: 20, height: 20, borderRadius: 10, flex: '0 0 auto',
                          border: `${active ? 6 : 2}px solid ${active ? colors.primary : colors.line}`,
                          transition: 'border .16s ease',
                        }}
                      />
                    </button>
                  </Reveal>
                );
              })}
            </Reveal>
          ) : null}

          {step === 1 ? (
            <Reveal>
              <h2 className="h2">Business details</h2>
              <p className="t-small" style={{ marginTop: 4, marginBottom: 16 }}>
                Customers see this on your profile.
              </p>

              <ImageUploader
                label="Business logo"
                folder="logo"
                hint="A clear logo or shop photo helps customers recognise you."
                value={logo}
                onChange={setLogo}
                multiple={false}
                size={96}
              />

              <Input
                label="Business / work name"
                icon="🏪"
                placeholder="e.g. Sharma Electricals"
                value={form.businessName}
                onChange={(e) => { setForm({ ...form, businessName: e.target.value }); setError(''); }}
              />
              <Input
                label="Contact number (optional)"
                icon="📞"
                inputMode="numeric"
                maxLength={10}
                placeholder="Leave blank to use your login number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
              />
              <Input
                label="About your work (optional)"
                textarea
                rows={3}
                placeholder="Tell customers what you do best"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />

              {VEHICLE_OPTIONS[roadType] ? (
                <>
                  <label className="field-label">
                    {roadType === 'courier' ? 'What do you deliver?' : 'What do you drive?'}
                  </label>
                  <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
                    {VEHICLE_OPTIONS[roadType].map((v) => (
                      <Chip
                        key={v.value}
                        label={v.label}
                        active={form.vehicleType === v.value}
                        onClick={() => setForm({ ...form, vehicleType: v.value })}
                      />
                    ))}
                  </div>
                  <div className="t-title" style={{ marginTop: 14 }}>Your vehicle</div>
                  <div className="t-tiny" style={{ marginBottom: 8 }}>Kept on record, shown to admin and to the customer you are picking up.</div>
                  <VehicleForm
                    type={form.vehicleType}
                    value={form.vehicle}
                    onChange={(vehicle) => setForm({ ...form, vehicle, vehicleModel: vehicle.model || '', vehicleNumber: vehicle.number || '' })}
                  />
                </>
              ) : null}

              <ImageUploader
                label="Photos of your shop or work"
                folder="gallery"
                value={gallery}
                onChange={setGallery}
                max={6}
              />

              <label className="field-label" style={{ marginTop: 12 }}>Categories you serve</label>
              <div className="t-tiny" style={{ marginBottom: 8 }}>
                {typeLabel
                  ? `Suggested for ${typeLabel}. Pick every category that applies.`
                  : 'Pick every category that applies.'}
              </div>
              <div className="row wrap" style={{ gap: 8 }}>
                {categories.map((c) => (
                  <Chip
                    key={c._id}
                    label={c.name}
                    icon={c.icon}
                    active={selected.includes(c._id)}
                    onClick={() => toggleCategory(c._id)}
                  />
                ))}
              </div>

              {customCats.length ? (
                <div className="row wrap" style={{ gap: 8, marginTop: 8 }}>
                  {customCats.map((c) => (
                    <Chip
                      key={c._id}
                      label={c.isActive ? c.name : `${c.name} · awaiting approval`}
                      icon={c.icon}
                      active={selected.includes(c._id)}
                      onClick={() => toggleCategory(c._id)}
                    />
                  ))}
                </div>
              ) : null}

              {roadType ? null : (
                <>
              <label className="field-label" style={{ marginTop: 14 }}>Can't find your service? Add it</label>
              <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                <input
                  className="input"
                  style={{ flex: 1 }}
                  placeholder="e.g. Birds seller, Honey farm"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); requestCustom(); } }}
                />
                <Button title="Add" variant="light" loading={requesting} onClick={requestCustom} />
              </div>
              <div className="t-tiny" style={{ marginTop: 4 }}>
                {customNote || 'KAAMWALA approves it or files it under the right category. You can start adding products right away.'}
              </div>
                </>
              )}

              {/*
                Our partner-type mapping cannot cover every real business - a
                kiryana shop that also runs tiffin, say - so the full customer
                catalogue stays one click away rather than being unreachable.
              */}
              {roadType ? null : (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="row-between"
                style={{
                  width: '100%', background: 'none', border: 0,
                  borderTop: `1px solid ${colors.line}`, padding: '12px 0', marginTop: 12,
                }}
              >
                <span className="t-small" style={{ color: colors.primary, fontWeight: 800 }}>
                  {showAll ? 'Hide other categories' : 'Browse all categories'}
                </span>
                <span style={{ color: colors.primary }}>{showAll ? '⌃' : '⌄'}</span>
              </button>
              )}

              {showAll ? (
                <Reveal>
                  {groups.map((g) => {
                    // Anything already offered above would just be a duplicate.
                    const rest = (g.items || []).filter(
                      (c) => !categories.some((rec) => rec._id === c._id),
                    );
                    if (!rest.length) return null;
                    return (
                      <div key={g.group} style={{ marginTop: 12 }}>
                        <div
                          className="t-tiny"
                          style={{ letterSpacing: 0.6, marginBottom: 8, color: colors.ink2, fontWeight: 800 }}
                        >
                          {g.group.toUpperCase()}
                        </div>
                        <div className="row wrap" style={{ gap: 8 }}>
                          {rest.map((c) => (
                            <Chip
                              key={c._id}
                              label={c.name}
                              icon={c.icon}
                              active={selected.includes(c._id)}
                              onClick={() => toggleCategory(c._id)}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </Reveal>
              ) : null}
              <OnboardingQuestions
                partnerType={form.partnerTypes.join(',')}
                categories={selected}
                answers={answers}
                onChange={setAnswers}
                onFields={setQuestions}
              />
            </Reveal>
          ) : null}

          {step === 2 ? (
            <Reveal>
              <h2 className="h2">{roadType ? 'Which city do you work in?' : 'Where do you operate?'}</h2>
              <p className="t-small" style={{ marginTop: 4, marginBottom: 16 }}>
                {roadType
                  ? 'Rides and parcels come from wherever you are at that moment - one trip can start in Delhi and end in Punjab - so we only need your city.'
                  : 'Customers nearby will discover you first.'}
              </p>
              {roadType ? null : (
                <>
                  <Input
                    label="Shop / work address"
                    icon="📍"
                    placeholder="Street, area"
                    value={form.line1}
                    onChange={(e) => { setForm({ ...form, line1: e.target.value }); setError(''); }}
                  />
                  <Input
                    label="Landmark (optional)"
                    placeholder="Near…"
                    value={form.landmark}
                    onChange={(e) => setForm({ ...form, landmark: e.target.value })}
                  />
                </>
              )}
              <CityPicker
                label="City"
                value={form}
                onChange={({ city, district, state, coordinates: c }) => {
                  setForm({ ...form, city, district, state });
                  if (c) setCityCoords(c);
                }}
              />
              <div className="t-tiny" style={{ margin: '8px 0 12px' }}>
                📍 {gpsState === 'ok'
                  ? (roadType
                      ? 'We use your live location while you are on duty - you are not listed as a shop anywhere.'
                      : 'Your business will be pinned at your current location.')
                  : gpsState === 'asking'
                    ? 'Finding your location…'
                    : cityCoords
                      ? `Location access is off - your business will be pinned at ${form.city}. Allow location for an exact pin.`
                      : 'Location access is off - allow it, or pick your city from the list, so customers and riders can find you.'}
              </div>
              <Input
                label="Pincode"
                inputMode="numeric"
                maxLength={6}
                value={form.pincode}
                onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '') })}
              />
              <div
                className="row"
                style={{
                  gap: 10, background: colors.primaryLight, padding: 12,
                  borderRadius: 12, alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 15 }}>🛡️</span>
                <span className="t-small" style={{ color: colors.ink2 }}>
                  Your business is reviewed by the KAAMWALA team before it goes live. You can add
                  products and services as soon as it is approved.
                </span>
              </div>
            </Reveal>
          ) : null}

          {error ? <div className="field-error" style={{ marginTop: 12 }}>{error}</div> : null}

          <Button
            title={step === 2 ? 'Submit for approval' : 'Continue'}
            onClick={step === 2 ? submit : next}
            loading={loading}
            size="lg"
            full
            style={{ marginTop: 20 }}
          />
        </div>
      </div>
    </div>
  );
};

export default PartnerRegisterPage;
